import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import AssetService from '../../Services/AssetService';
import AssetAlarmsTabDisplay from './AssetAlarmsTabDisplay';
import RequestHelper from '../../Lib/AlertAndDmRequestHelper';

const SORT_DIRECTIONS = ['ASC', 'DESC'];
const limit = 20;

const initState = {
  alertToClear: {},
  dataLoading: true,
  clearRequestPending: false,
  selectedStatus: 'positive',
  ruleIdFilter: null,
  sort: 'DetectedAt',
  severityFilter: 'critical',
  order: 'DESC',
  page: 1,
  dataCount: 0,
  data: [],
  error: '',
};

const refreshState = {
  dataLoading: true,
  page: 1,
  dataCount: 0,
  data: [],
};

export default function AssetAlarmsContainer(props) {
  const {
    asset: { _id: assetID, orgID },
    updateActiveAlertCount,
  } = props;

  const [state, updateState] = useState(initState);
  const [error, setError] = useState('');
  const [showMobileFilters, toggleMobileFilters] = useState(false);
  const [showClearAlertModal, toggleClearAlertModal] = useState(false);
  const ASSET_NOT_FOUND_ERROR = 'Failed to get template';
  const assetService = new AssetService();
  const history = useHistory();
  let source = CancelToken.source();

  const handleRuleLinkClick = async (assetID, ruleID) => {
    try {
      updateState({ ...state, dataLoading: true });
      const { _id } = await assetService.fetchModelByAssetId(
        assetID,
        source.token
      );
      history.push({
        pathname: `/asset-model/${_id}`,
        search: `?tab=editRule&RuleId=${ruleID}`,
      });
    } catch (err) {
      if (isCancel(err)) return;
      let { message } = err.response?.data ?? err;
      if (message === ASSET_NOT_FOUND_ERROR) {
        message =
          'This rule was created on an asset that was either moved or no longer exists.';
      }
      setError(message);
      updateState({ ...state, dataLoading: false });
    }
  };

  const handleRuleSelected = (selected, mobileView) => {
    const { value: ruleIdFilter } = selected || {};
    updateState({ ...state, ...refreshState, ruleIdFilter });
    mobileView && toggleMobileFilters(false);
  };

  const handleSortClick = (nextSortBy) => {
    const { sort } = state;
    switch (true) {
      case nextSortBy === sort: {
        const [order] = SORT_DIRECTIONS.filter((d) => d !== state.order);
        updateAlertsState({ page: 1, order });
        break;
      }
      default:
        updateAlertsState({
          page: 1,
          sort: nextSortBy,
          order: 'DESC',
        });
    }
  };

  const setSelectedStatus = (selectedStatus, mobileView) => {
    if (selectedStatus === state.selectedStatus) return;
    updateState({ ...state, ...refreshState, selectedStatus });
    mobileView && toggleMobileFilters(false);
  };

  const setPage = (page) => {
    if (page === state.page) return;
    updateAlertsState({ page });
  };

  const fetchAlertMetrics = async () => {
    try {
      const request = new RequestHelper({
        ...state,
        orgID,
        typeFilter: 'rule',
        gatewayIdFilter: assetID,
      });
      const { Count } = await request.fetchAlertMetrics(source.token);
      return Count || 0;
    } catch (err) {
      if (!isCancel(err)) {
        setError(err.response?.data?.message ?? err.message);
      }
      return 0;
    }
  };

  const fetchAlerts = async ({ page, sort, order } = {}) => {
    try {
      const request = new RequestHelper({
        ...state,
        orgID,
        typeFilter: 'rule',
        severityFilter: 'critical',
        gatewayIdFilter: assetID,
        page: page || state.page,
        sort: sort || state.sort,
        order: order || state.order,
        limit,
      });
      const Alerts = await request.fetchAlerts(source.token);
      return Alerts || [];
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
    }
  };

  const toggleClearAlertModalClick = (alert) => {
    updateState({ ...state, alertToClear: alert || {} });
    setError('');
    toggleClearAlertModal(!showClearAlertModal);
  };

  const handleClearAlertClick = async () => {
    updateState({ ...state, clearRequestPending: true });
    try {
      const request = new RequestHelper({
        alertID: state.alertToClear.Id,
        orgID: state.alertToClear.OrgId,
      });
      await request.clearAlert(source.token);
      updateCountAndAlertsState();
      toggleClearAlertModal(!showClearAlertModal);
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
    }
  };

  const updateCountAndAlertsState = async () => {
    try {
      const [dataCount, data] = await Promise.all([
        fetchAlertMetrics(),
        fetchAlerts(),
      ]);
      updateState({
        ...state,
        dataCount,
        data,
        dataLoading: false,
        alertToClear: {},
        clearRequestPending: false,
      });
      updateActiveAlertCount(dataCount);
    } catch (err) {
      setError(err.message);
      updateState({ ...state, dataLoading: false });
    }
  };

  const updateAlertsState = async (params) => {
    try {
      const data = await fetchAlerts(params);
      updateState({ ...state, data, ...params, dataLoading: false });
    } catch (err) {
      setError(err.message);
      updateState({ ...state, dataLoading: false });
    }
  };

  useEffect(() => {
    if (!assetID) return;
    source = CancelToken.source();
    updateCountAndAlertsState();
    return source.cancel;
  }, [assetID, state.selectedStatus, state.ruleIdFilter]);

  return (
    <AssetAlarmsTabDisplay
      {...state}
      {...props}
      tab="alerts"
      handleStatusSelected={setSelectedStatus}
      handleRuleSelected={handleRuleSelected}
      handleRuleLinkClick={handleRuleLinkClick}
      handleClearAlertClick={handleClearAlertClick}
      toggleClearAlertModalClick={toggleClearAlertModalClick}
      showClearAlertModal={showClearAlertModal}
      clearRequestPending={state.clearRequestPending}
      sort={{ by: state.sort, direction: state.order }}
      handleSortClick={handleSortClick}
      dataLoading={state.dataLoading}
      pages={Math.ceil(state.dataCount / limit)}
      handlePageChange={setPage}
      error={error}
      setError={setError}
      showMobileFilters={showMobileFilters}
      toggleMobileFilters={toggleMobileFilters}
    />
  );
}

AssetAlarmsContainer.propTypes = {
  asset: PropTypes.object,
  currentUser: PropTypes.object,
  currentOrg: PropTypes.object,
  orgsMap: PropTypes.object,
  updateActiveAlertCount: PropTypes.func,
  width: PropTypes.number,
};

AssetAlarmsContainer.defaultProps = { currentUser: {} };
